import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnInit, signal, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import {
    ApiError,
    DatepickerComponent,
    DateUtilPipe,
    DateUtilService,
    LoadingOverlayComponent,
    MAT_ICON,
    Material,
    SelectComponent,
    SelectOption,
    SessionPreferenceService,
    TOAST_STATE,
    ToastmessageService,
    TranslationPipe,
    TranslationService,
    Void
} from '@prekog/common-ui';
import { DecimalPipe } from '@angular/common';
import { catchError, EMPTY, forkJoin, Observable } from 'rxjs';
import { FUTURE_DATE_ERROR, INSUFFICIENT_STOCK_ERROR, SUCCESSFULLY_SAVED } from 'src/app/shared/adminconstants';
import { Festival } from 'src/app/shared/festival';
import { ReferenceDataService } from 'src/app/shared/reference-data.service';
import { getStorageTypeDisplayValue, StorageType } from 'src/app/shared/storagetype.enum';
import { Partner } from '../../shared/partner';
import { ProductEventType } from '../../shared/producteventtype.enum';
import { ProductEvent } from '../productevent';
import { FormStateConfig, PRODUCT_EVENT_FORM_RULES } from '../producteventform.config';
import { ProductEventFormHelperService } from '../producteventformhelper.service';
import { ProductEventRecalculationInput, RecalculationService } from '../recalculation.service';
import { AddProductEventLastSelection } from './addproductevent-last-selection.model';
import { AddProductEventRequest } from './addproductevent.request';
import { ProductEventRegistration } from './producteventregistration';
import { AddProductEventUseCase } from '../../use-cases/addproductevent/add-product-event.usecase';

const SELECTABLE_STORAGE_TYPES: (keyof typeof StorageType)[] = ['OFFICE', 'MULTIVERZUM_WAREHOUSE'];

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-addproductevent',
    templateUrl: './addproductevent.component.html',
    styleUrls: ['./addproductevent.component.scss'],
    standalone: true,
    imports: [
        MatIconModule,
        TranslationPipe,
        ReactiveFormsModule,
        DateUtilPipe,
        DecimalPipe,
        DatepickerComponent,
        SelectComponent,
        LoadingOverlayComponent
    ]
})
export class AddProductEventComponent implements OnInit, AfterViewInit {
    activeConfig: FormStateConfig = PRODUCT_EVENT_FORM_RULES['DEFAULT'];
    addProductEvent!: FormGroup;
    defaultEventDate!: string;
    readonly editingIndex = signal<number | null>(null);
    eventTypeKeys: (keyof typeof ProductEventType)[] = [];
    readonly eventTypeOptions = signal<SelectOption[]>([]);
    festivals: Festival[] = [];
    readonly festivalSelectOptions = signal<SelectOption[]>([]);
    readonly firstInputField = viewChild.required<SelectComponent>('firstField');
    readonly initialFestivalId = signal<number | string | null>(null);
    readonly initialFromStorageTypeId = signal<number | string | null>(null);
    readonly initialPartnerId = signal<number | string | null>(null);
    readonly initialProductId = signal<number | string | null>(null);
    readonly initialToStorageTypeId = signal<number | string | null>(null);
    lastSelectedProductId: number | null = null;
    maxDate!: string;
    partners: Partner[] = [];
    readonly partnerSelectOptions = signal<SelectOption[]>([]);
    productEventDisplayList: ProductEvent[] = [];
    productEventRegistrationList: ProductEventRegistration[] = [];
    productEventType = ProductEventType;
    products: Material[] = [];
    readonly productSelectOptions = signal<SelectOption[]>([]);
    selectedProductEventTypeOption: string | null = null;
    storageType = StorageType;
    storageTypeKeys: (keyof typeof StorageType)[] = [];
    readonly storageTypeOptions = signal<SelectOption[]>([]);
    private _addProductEventUseCase = inject(AddProductEventUseCase);
    private _fb = inject(FormBuilder);
    private _router = inject(Router);
    private _translateService = inject(TranslationService);
    private _toastMessageService = inject(ToastmessageService);
    private _location = inject(Location);
    private _formHelper = inject(ProductEventFormHelperService);
    private _dateUtilService = inject(DateUtilService);
    private _recalculationService = inject(RecalculationService);
    private _sessionPreference = inject(SessionPreferenceService);
    private _referenceData = inject(ReferenceDataService);

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            void this._router.navigateByUrl('/');
        } else {
            this.eventTypeKeys = this._getProductEventTypeEnumKeys(this.productEventType);
            this.storageTypeKeys = SELECTABLE_STORAGE_TYPES;
            this._buildEventTypeOptions();
            this._buildStorageTypeOptions();
            this._loadReferenceData();
            this.maxDate = this._dateUtilService.getMaxDateForDateSelection(true);
            this.defaultEventDate = this._buildTodayString();
            this._buildProductEventForm();
            const savedSelection = this._sessionPreference.load<AddProductEventLastSelection>('addproductevent-last-selection');
            if (savedSelection) {
                this.addProductEvent.patchValue(savedSelection);
            }
        }
    }

    ngAfterViewInit(): void {
        this.firstInputField().triggerElement()?.nativeElement?.focus();
    }

    navigateBack(): void {
        this._location.back();
    }

    onEventTypeSelect(id: number | string | null): void {
        this.addProductEvent.get('productEventType')?.setValue(id);
    }

    onFromStorageTypeSelect(id: number | string | null): void {
        this.addProductEvent.get('fromStorageType')?.setValue(id);
    }

    onToStorageTypeSelect(id: number | string | null): void {
        this.addProductEvent.get('toStorageType')?.setValue(id);
    }

    onProductSelect(id: number | string | null): void {
        this.addProductEvent.get('productId')?.setValue(id);
    }

    onFestivalSelect(id: number | string | null): void {
        this.addProductEvent.get('festivalId')?.setValue(id);
    }

    onPartnerSelect(id: number | string | null): void {
        this.addProductEvent.get('partnerId')?.setValue(id);
    }

    onEventDateSelected(date: Date | null): void {
        const value = date
            ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
            : '';
        this.addProductEvent.get('eventDate')?.setValue(value);
    }

    isFieldVisible(controlName: string): boolean {
        if (!this.activeConfig?.visible || !this.activeConfig.visible.includes(controlName)) {
            return false;
        }
        const control = this.addProductEvent.get(controlName);
        return control ? control.enabled : false;
    }

    onSubmitListElement(): void {
        if (this.editingIndex() !== null) {
            this._updateElementInList();
        } else {
            this._addElementToList();
        }
    }

    getProductEventTypeDisplayValue(enumMember: ProductEventType | undefined): string {
        if (!enumMember) return 'EmptyValue';
        return this.getEnumMemberByValue(ProductEventType, enumMember) ?? 'Missing Type!';
    }

    getStorageTypeDisplayValue(enumMember: StorageType | undefined): string | null {
        return getStorageTypeDisplayValue(enumMember);
    }

    getEnumMemberByValue(enumObj: typeof ProductEventType, searchValue: string): string | null {
        const entry = Object.entries(enumObj).find(([, value]) => value === searchValue);
        return entry ? entry[0] : 'EmptyValue';
    }

    onSaveList(): void {
        const createProductEventRequest: AddProductEventRequest = {
            productEventRegistrationList: this.productEventRegistrationList
        };
        (this._addProductEventUseCase.execute(createProductEventRequest) as Observable<Void>)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    const apiError: ApiError = error.error as ApiError;
                    if (
                        apiError?.errorMessage === INSUFFICIENT_STOCK_ERROR
                        && apiError.messageVariableList?.length
                    ) {
                        const failingIndex = Number(apiError.messageVariableList[0]);
                        const failingElement = this.productEventDisplayList[failingIndex];
                        const storageTypeEnumValue =
                            StorageType[failingElement.fromStorageType as unknown as keyof typeof StorageType];
                        const storageTypeDisplayName = this._translateService.translate(
                            'admin.product.statistics.storage.' + storageTypeEnumValue
                        );
                        const entityName = failingElement.festivalName
                            ?? failingElement.partnerName
                            ?? '';
                        const storageLabel = entityName
                            ? `${storageTypeDisplayName} (${entityName})`
                            : storageTypeDisplayName;
                        const translatedMessage = this._translateService
                            .translate(INSUFFICIENT_STOCK_ERROR)
                            .replace('{{storageType}}', storageLabel);
                        this._toastMessageService.displayToastMessage(
                            TOAST_STATE.error,
                            translatedMessage,
                            MAT_ICON.error
                        );
                        this.onEditListElement(failingIndex);
                    } else {
                        this._toastMessageService.displayToastMessage(
                            TOAST_STATE.error,
                            apiError?.errorMessage ?? 'Unknown error',
                            MAT_ICON.error
                        );
                    }
                    return EMPTY;
                })
            )
            .subscribe(() => {
                this._triggerRecalculation();
                this._toastMessageService.displayToastMessage(
                    TOAST_STATE.success,
                    this._translateService.translate(SUCCESSFULLY_SAVED),
                    MAT_ICON.success
                );
                this._toastMessageService.dismissToast();
                void this._router.navigateByUrl('/productevents');
            });
    }

    onEditListElement(index: number): void {
        const editItem = this.productEventDisplayList[index];

        this.editingIndex.set(index);

        this.addProductEvent.get('productEventType')?.setValue(editItem.productEventType);
        this.firstInputField().selectedId.set(editItem.productEventType ?? null);

        this.addProductEvent.patchValue({
            fromStorageType: editItem.fromStorageType,
            toStorageType: editItem.toStorageType,
            festivalId: editItem.festivalId,
            productId: editItem.productId,
            partnerId: editItem.partnerId,
            orderDetailsReference: editItem.referenceNumber,
            productReference: editItem.referenceNumber,
            quantity: editItem.quantity,
            unitPrice: editItem.unitPrice,
            eventDate: editItem.eventDate,
            comment: editItem.comment
        });

        this.initialFromStorageTypeId.set(editItem.fromStorageType ?? null);
        this.initialToStorageTypeId.set(editItem.toStorageType ?? null);
        this.initialProductId.set(editItem.productId ?? null);
        this.initialFestivalId.set(editItem.festivalId ?? null);
        this.initialPartnerId.set(editItem.partnerId ?? null);
    }

    onDeleteListElement(index: number): void {
        this.productEventDisplayList.splice(index, 1);
        this.productEventRegistrationList.splice(index, 1);
    }

    toDateArray(value: Date | string | null | undefined): number[] | null {
        if (!value) return null;
        if (value instanceof Date) {
            return [value.getFullYear(), value.getMonth() + 1, value.getDate()];
        }
        const parts = String(value).split('-').map(Number);
        return parts.length === 3 ? parts : null;
    }

    private _getProductEventTypeEnumKeys(enumObject: Record<string, string>): (keyof typeof ProductEventType)[] {
        return Object.keys(enumObject).filter(
            (key) => isNaN(Number(key)) && key !== 'SOLD_VIA_WEBSHOP'
        ) as (keyof typeof ProductEventType)[];
    }

    private _buildProductEventForm(): void {
        const futureDateValidator = this._dateUtilService.futureDateValidator(() => this._showFutureDateError());

        this.addProductEvent = this._fb.group({
            productEventType: [null, Validators.required],
            fromStorageType: [null, Validators.required],
            toStorageType: [null, Validators.required],
            festivalId: [null, [Validators.required]],
            productId: [this.lastSelectedProductId, [Validators.required]],
            partnerId: [null, [Validators.required]],
            orderDetailsReference: [null],
            productReference: [null, [Validators.required]],
            quantity: [0, [Validators.required]],
            unitPrice: [0, [Validators.required]],
            eventDate: [this.defaultEventDate, [Validators.required, futureDateValidator]],
            comment: [null]
        });

        this.addProductEvent.get('productEventType')?.valueChanges.subscribe((value: string | null) => {
            this.selectedProductEventTypeOption = value;
            this._toggleConditionalField(value);
        });
    }

    private _showFutureDateError(): void {
        this._toastMessageService.displayToastMessage(
            TOAST_STATE.error,
            this._translateService.translate(FUTURE_DATE_ERROR),
            MAT_ICON.error
        );
    }

    private _toggleConditionalField(value: string | null): void {
        this.activeConfig =
            value && PRODUCT_EVENT_FORM_RULES[value] ? PRODUCT_EVENT_FORM_RULES[value] : PRODUCT_EVENT_FORM_RULES['DEFAULT'];

        this._formHelper.applyFormRules(this.addProductEvent, value);

        this.initialFromStorageTypeId.set(this.addProductEvent.get('fromStorageType')?.value ?? null);
        this.initialToStorageTypeId.set(this.addProductEvent.get('toStorageType')?.value ?? null);
        this.initialProductId.set(this.addProductEvent.get('productId')?.value ?? null);
        this.initialFestivalId.set(this.addProductEvent.get('festivalId')?.value ?? null);
        this.initialPartnerId.set(this.addProductEvent.get('partnerId')?.value ?? null);
    }

    private _addElementToList(): void {
        const { productEventEntry, productEventDisplayEntry } = this._buildProductEventEntry();
        this.productEventDisplayList.push(productEventDisplayEntry);
        this.productEventRegistrationList.push(productEventEntry);
        this._finalizeListSubmission(productEventEntry);
    }

    private _updateElementInList(): void {
        const currentEditingIndex = this.editingIndex()!;
        const { productEventEntry, productEventDisplayEntry } = this._buildProductEventEntry();
        this.productEventDisplayList[currentEditingIndex] = productEventDisplayEntry;
        this.productEventRegistrationList[currentEditingIndex] = productEventEntry;
        this.editingIndex.set(null);
        this._finalizeListSubmission(productEventEntry);
    }

    private _buildProductEventEntry(): { productEventEntry: any; productEventDisplayEntry: any } {
        const productEvent = this.addProductEvent.value;
        const rawProductEventData = this.addProductEvent.getRawValue();
        const productEventDisplay = structuredClone(productEvent);
        const festival = this.festivals.find((festival) => festival.id === productEvent.festivalId);
        const partner = this.partners.find((partner) => partner.id === productEvent.partnerId);
        const product = this.products.find((product) => product.id === productEvent.productId);
        const type = this.eventTypeKeys.find((eventKey) => eventKey[0] === productEvent.productEventType);
        const fromStorageType = rawProductEventData.fromStorageType;
        const toStorageType = rawProductEventData.toStorageType;
        const referenceNumber = productEvent.productReference
            ? productEvent.productReference
            : productEvent.orderDetailsReference;

        if (type) {
            const productEventType = this.getEnumMemberByValue(ProductEventType, type);
            productEvent.productEventType = productEventType;
            productEventDisplay.productEventType = productEventType;
        }

        productEvent.fromStorageType = fromStorageType;
        productEvent.toStorageType = toStorageType;
        productEventDisplay.fromStorageType = fromStorageType;
        productEventDisplay.toStorageType = toStorageType;
        productEventDisplay.referenceNumber = referenceNumber;
        productEventDisplay.partnerName = partner?.name;
        productEventDisplay.festivalName = festival?.name;
        productEventDisplay.productName = product?.title + ' - ' + product?.author + '[' + product?.isbn + ']';

        return { productEventEntry: productEvent, productEventDisplayEntry: productEventDisplay };
    }

    private _finalizeListSubmission(productEvent: any): void {
        this.lastSelectedProductId = productEvent.productId;
        this._sessionPreference.save<AddProductEventLastSelection>('addproductevent-last-selection', {
            productId: productEvent.productId,
            partnerId: productEvent.partnerId,
            festivalId: productEvent.festivalId
        });
        this.addProductEvent.reset();
        this._buildProductEventForm();
        this._resetSelectSignals();
        this.firstInputField().selectedId.set(null);
    }

    private _buildEventTypeOptions(): void {
        this.eventTypeOptions.set(
            this.eventTypeKeys.map((key) => ({
                id: this.productEventType[key],
                name: this._translateService.translate('admin.product.event.type.' + key)
            }))
        );
    }

    private _buildStorageTypeOptions(): void {
        this.storageTypeOptions.set(
            this.storageTypeKeys.map((key) => ({
                id: key,
                name: this._translateService.translate('admin.product.statistics.storage.' + this.storageType[key])
            }))
        );
    }

    private _loadReferenceData(): void {
        forkJoin({
            products: this._referenceData.loadProducts(),
            partners: this._referenceData.loadPartners(),
            festivals: this._referenceData.loadFestivals()
        }).subscribe((responses) => {
            this.products = responses.products;
            this.productSelectOptions.set(responses.products.map((p) => ({ id: p.id!, name: `${p.title} - ${p.author} [${p.isbn}]` })));
            this.partners = responses.partners;
            this.partnerSelectOptions.set(responses.partners.map((p) => ({ id: p.id!, name: p.name! })));
            this.festivals = responses.festivals;
            this.festivalSelectOptions.set(responses.festivals.map((f) => ({ id: f.id!, name: f.name! })));
        });
    }

    private _triggerRecalculation(): void {
        const events: ProductEventRecalculationInput[] = this.productEventRegistrationList
            .filter((e) => e.productId !== undefined && e.productId !== null && e.eventDate !== undefined && e.eventDate !== null)
            .map((e) => ({ productId: e.productId!, eventDate: String(e.eventDate) }));
        const request = this._recalculationService.buildBatchRecalculationRequest(events);
        if (request) {
            this._recalculationService.dispatchRecalculation(request);
        }
    }

    private _buildTodayString(): string {
        const now = new Date();
        return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
    }

    private _resetSelectSignals(): void {
        this.initialFromStorageTypeId.set(null);
        this.initialToStorageTypeId.set(null);
        this.initialProductId.set(this.lastSelectedProductId);
        this.initialFestivalId.set(null);
        this.initialPartnerId.set(null);
    }
}
